import { ProfileDto } from './socialService';

export interface CachedProfileRecord {
  version: 1;
  cachedAt: number;
  profile: ProfileDto;
  canEdit: boolean;
  /** ETag from last full profile GET — used for If-None-Match revalidation. */
  etag?: string;
}

export const PROFILE_CACHE_STALE_MS = 24 * 60 * 60 * 1000;

const CACHE_VERSION = 1 as const;

export function profileCacheKey(viewerUsername: string, aliasName: string): string {
  return `profile_cache:v${CACHE_VERSION}:${viewerUsername.trim().toLowerCase()}:${aliasName.trim().toLowerCase()}`;
}

export function readCachedProfile(viewerUsername: string, aliasName: string): CachedProfileRecord | null {
  if (!viewerUsername?.trim() || !aliasName?.trim()) return null;
  try {
    const raw = localStorage.getItem(profileCacheKey(viewerUsername, aliasName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedProfileRecord;
    if (parsed?.version !== CACHE_VERSION || !parsed.profile) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedProfile(
  viewerUsername: string,
  aliasName: string,
  profile: ProfileDto,
  canEdit: boolean,
  etag?: string,
): void {
  if (!viewerUsername?.trim() || !aliasName?.trim()) return;
  const record: CachedProfileRecord = {
    version: CACHE_VERSION,
    cachedAt: Date.now(),
    profile,
    canEdit,
    etag,
  };
  try {
    localStorage.setItem(profileCacheKey(viewerUsername, aliasName), JSON.stringify(record));
  } catch {
    // Ignore quota failures.
  }
}

export function isProfileCacheStale(cachedAt: number): boolean {
  return Date.now() - cachedAt > PROFILE_CACHE_STALE_MS;
}
